import { Link } from 'react-router-dom';
import TeamBadge from '@/components/base/TeamBadge';
import { getTeam } from '@/utils/data';
import type { Match } from '@/types';

const statusText: Record<string, string> = {
  finished: '已完赛',
  live: '进行中',
  upcoming: '未开始',
};

const statusStyle: Record<string, string> = {
  finished: 'bg-secondary-100 text-secondary-800',
  live: 'bg-primary-100 text-primary-700',
  upcoming: 'bg-accent-100 text-accent-900',
};

export default function MatchRow({ match, showLeague = true }: { match: Match; showLeague?: boolean }) {
  const home = getTeam(match.homeTeamId);
  const away = getTeam(match.awayTeamId);
  const hasScore = match.status !== 'upcoming';
  const homeWin = hasScore && match.homeScore > match.awayScore;
  const awayWin = hasScore && match.awayScore > match.homeScore;

  return (
    <Link
      to={`/detail/${match.id}`}
      className="block group cursor-pointer px-3 md:px-4 py-3 border-b border-background-200/70 last:border-b-0 hover:bg-primary-50/60 transition-colors"
    >
      <div className="flex items-center gap-3 md:gap-4">
        <div className="w-14 md:w-16 shrink-0 flex flex-col items-center gap-1">
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${statusStyle[match.status]}`}>
            {match.status === 'live' && <i className="ri-live-line animate-pulse-live"></i>}
            {statusText[match.status]}
          </span>
          <span className="text-[10px] md:text-xs text-foreground-500 font-label">{match.date} {match.time}</span>
        </div>

        <div className="flex-1 min-w-0">
          {showLeague && (
            <div className="text-[10px] md:text-xs text-foreground-500 mb-1 truncate">
              {match.leagueName} · {match.stage}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <TeamBadge team={home} size="sm" />
              <span className={`text-sm md:text-[15px] truncate ${homeWin ? 'font-bold text-foreground-950' : 'text-foreground-700'}`}>
                {home?.name}
              </span>
            </div>
            <span className={`font-heading font-bold text-base md:text-lg tabular-nums ${homeWin ? 'text-primary-600' : 'text-foreground-700'}`}>
              {hasScore ? match.homeScore : '-'}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <TeamBadge team={away} size="sm" />
              <span className={`text-sm md:text-[15px] truncate ${awayWin ? 'font-bold text-foreground-950' : 'text-foreground-700'}`}>
                {away?.name}
              </span>
            </div>
            <span className={`font-heading font-bold text-base md:text-lg tabular-nums ${awayWin ? 'text-primary-600' : 'text-foreground-700'}`}>
              {hasScore ? match.awayScore : '-'}
            </span>
          </div>
        </div>

        <span className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-300 group-hover:text-primary-500 transition-colors">
          <i className="ri-arrow-right-s-line text-lg"></i>
        </span>
      </div>
      {!showLeague && (
        <div className="mt-2 ml-[68px] md:ml-[80px] text-[10px] md:text-xs text-foreground-500 flex items-center gap-2">
          <span>{match.stage}</span>
          {match.status === 'live' && <span className="text-primary-600">· {match.progress}</span>}
        </div>
      )}
    </Link>
  );
}